import { Observable } from 'rxjs';
import { PagedList } from '../data/models/paged-list.model';
import { Provider } from '../data/models/provider.model';
import { SortModel } from '../data/models/sort-model.model';
import {
  ProviderCallOutcomeRequest,
  ProviderCallOutcomeResponse,
  RideshareOutboundPhoneNumber,
  RideshareProvider,
} from '../data/models/provider-call-outcome.model';
import httpService from './httpService';

class ProviderService {
  private readonly apiBaseTransport;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBaseTransport = __environment__.REACT_APP_PROVIDER_API_URL.trim();
  }

  private convertFilter(filter: string): string {
    const parts = filter.split('&').sort();
    const cttParts = parts.filter((p) => p.startsWith('ctt'));
    const insParts = parts.filter((p) => p.startsWith('ins'));
    const svcParts = parts.filter((p) => p.startsWith('svc'));
    const activeParts = parts.filter((p) => p.startsWith('active'));
    let f = '';
    if (cttParts.length === 1) {
      f += 'ctt-' + cttParts[0].substring(3);
    }
    if (insParts.length === 1) {
      if (f.length) {
        f += '~';
      }
      f += 'ins-' + insParts[0].substring(3);
    }
    if (svcParts.length > 0) {
      if (f.length) {
        f += '~';
      }
      f += 'svc';
      svcParts.forEach((svc) => {
        f += '-' + svc.substring(3);
      });
    }
    if (activeParts.length > 0) {
      if (f.length) {
        f += '~';
      }
      f += 'active';
      activeParts.forEach((svc) => {
        f += '-' + svc.substring(6);
      });
    }
    return f;
  }

  public findProviders({
    pageSize,
    page,
    filter,
    search,
    sortModel,
    tiers,
    transportationProviderIDs,
  }: {
    pageSize: number;
    page: number;
    filter: string;
    search: string;
    sortModel: SortModel | null;
    tiers?: string;
    transportationProviderIDs?: number[];
  }): Observable<PagedList<Provider>> {
    const f = encodeURIComponent(this.convertFilter(filter));
    const s = encodeURIComponent(search);
    // let url = `${this.apiBaseTransport}/optimized?page=${page}&pageSize=${pageSize}&filters=${f}&searchProviders=${s}`;
    let url = `${this.apiBaseTransport}/optimized?page=${page}&pageSize=${pageSize}&filters=${f}&searchProviders=${s}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    if (tiers) {
      url += `&providerTierID=${tiers}`;
    }
    if (transportationProviderIDs) {
      url += `&transportationProviderIDs=${transportationProviderIDs.join(',')}`;
    }
    return httpService.getJSON(url);
  }

  public getTransportApiInfo(): Observable<string> {
    const apiUrl = this.apiBaseTransport.split('providers')?.[0];
    const url = `${apiUrl}/health-check`;
    return httpService.getJSON(url);
  }

  public getCoverageAreas(stateCode: string, zipCode: string) {
    return httpService.getJSON(
      `${this.apiBaseTransport}/coverageAreas?state_code=${stateCode}&zipcode=${zipCode}`,
    );
  }

  public getProvider(providerID: number): Observable<Provider> {
    return httpService.getJSON(`${this.apiBaseTransport}/${providerID}`);
  }

  public getProviderCallOutcomes({
    transportationProviderTier,
    tripLegReferenceID,
    search,
    page,
    pageSize,
    sortModel,
    hasCallOutcome,
  }: ProviderCallOutcomeRequest): Observable<
    PagedList<ProviderCallOutcomeResponse>
  > {
    let url = `${this.apiBaseTransport}/call-outcomes?transportationProviderTier=${transportationProviderTier}&tripLegReferenceID=${tripLegReferenceID}&page=${page}&pageSize=${pageSize}&sort=${sortModel.column}&sortOrder=${sortModel.direction?.toUpperCase() ?? 'DESC'}`;
    if (search) {
      url += `&search=${search}`;
    }
    if (hasCallOutcome) {
      url += `&hasCallOutcome=${hasCallOutcome}`;
    }
    return httpService.getJSON(url);
  }

  public getRideshareOutboundPhoneNumbers() {
    return httpService.getJSON(
      `${this.apiBaseTransport}/rideshare/outbound-phones`,
    );
  }

  public updateRideshareOutboundPhoneNumbers(
    phoneNumber: RideshareOutboundPhoneNumber,
  ) {
    return httpService.patchJSON(
      `${this.apiBaseTransport}/rideshare/outbound-phones`,
      phoneNumber,
    );
  }

  public deleteRideshareOutboundPhoneNumbers(
    rideshareProvider: RideshareProvider,
  ) {
    return httpService.deleteJSON(
      `${this.apiBaseTransport}/rideshare/outbound-phones/${rideshareProvider}`,
    );
  }
}

const providerService = new ProviderService();

export default providerService;
